import { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { AutoComplete } from "primereact/autocomplete";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { getSocket } from "../context/socket";
import moment from "moment-timezone";
import { InputTextarea } from "primereact/inputtextarea";
import CustomToast from "./CustomToast";

const ChecklistPopup = ({ onCancel,  cardId, boardId }) => {
    const socket = getSocket();
  const toastRef = useRef(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("NA");
    const [dueDate, setDueDate] = useState(null);
    const [reminderDate, setReminderDate] = useState(null);
    const [priority, setPriority] = useState("Low");
    const [users, setUsers] = useState([]);
    const [items, setItems] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const showMessage = (severity, detail) => {
        const summary = severity.charAt(0).toUpperCase() + severity.slice(1);
        toastRef.current.show(severity, summary, detail);
      };


    // 🔄 Fetch eligible users on mount
    useEffect(() => {
        if (socket) {
            socket.emit("fetchFilteredUsers", { board_id: boardId }); // ✅ send board_id

            socket.on("filteredUsersList", (data) => {
                const usersWithFullName = data.map(user => ({
                    ...user,
                    fullName: `${user.first_name} ${user.last_name}`,
                }));
                setUsers(usersWithFullName);
            });

            socket.on("errorFetchingUsers", (err) => console.error(err));
        }

        return () => {
            socket?.off("filteredUsersList");
            socket?.off("errorFetchingUsers");
        };
    }, [socket]);

    // 🔎 Autocomplete logic
    const search = (event) => {
        const query = event.query.toLowerCase();
        const filtered = users.filter((user) =>
            user.fullName.toLowerCase().includes(query)
        );
        setItems(filtered);
    };


    // 📝 Submit checklist
    const handleAddChecklist = () => {
        if (!title || !selectedUser) {  
            showMessage('error', 'Title and Assignee are required.');
            return;
        }
        if (!dueDate || !reminderDate) {
            showMessage('error', 'Due Date and Reminder Date are required.');
            return;
        }


        const dueDateIST = dueDate
            ? moment(dueDate).subtract(5, 'hours').subtract(30, 'minutes').format("YYYY-MM-DD HH:mm:ss")
            : null;

        const reminderDateIST = reminderDate
            ? moment(reminderDate).startOf('day').subtract(5, 'hours').subtract(30, 'minutes').format("YYYY-MM-DD HH:mm:ss")
            : null;

        const checklistData = {
            card_id: cardId,
            name: title,
            description,
            is_checked: false,
            due_date: dueDateIST,
            reminder_date: reminderDateIST,
            priority,
            members: [selectedUser.user_id] // single user
        };

        socket.emit("addChecklist", checklistData);
        onCancel();
    };

    return (
        <div className="popup-box popup-checklist">
            <div className="popup-header">
                Add SubTask
                <Button
                    icon="pi pi-times"
                    className="p-button-text close-btn"
                    onClick={() => onCancel()}
                />
            </div>
            <div className="popup-body">
                <div className="p-fluid">
                    <label>Title</label>
                    <InputText value={title} onChange={(e) => setTitle(e.target.value)} required/>

                    <label className="mt-3">Description</label>
                    <InputTextarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="h-auto"/>

                    <label className="mt-3">Due Date</label>
                    <Calendar value={dueDate} onChange={(e) => setDueDate(e.value)} required />

                    <label className="mt-3">Reminder Date</label>
                    <Calendar value={reminderDate} onChange={(e) => setReminderDate(e.value)} required />

                    <label className="mt-3">Priority</label>
                    <Dropdown
                        value={priority}
                        options={["Low", "Medium", "High", "Urgent"]}
                        onChange={(e) => setPriority(e.value)}
                        placeholder="Select Priority"
                        required
                    />

                    <label className="mt-3">Assign To</label>

                    {!selectedUser ? (
                        <AutoComplete
                            field="fullName"
                            value={selectedUser}
                            suggestions={items}
                            completeMethod={search}
                            onSelect={(e) => {
                                setSelectedUser(e.value);  // ✅ Set only when user selects
                                setItems([]);              // ✅ Clear suggestions
                            }}
                            itemTemplate={(item) => (
                                <div className="flex align-items-center gap-3">
                                    <img
                                        src={item?.profileImage?.image_url || "https://primefaces.org/cdn/primereact/images/avatar/amyelsner.png"}
                                        alt={item.first_name}
                                        style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                                    />
                                    <span>{item.fullName}</span>
                                </div>
                            )}
                            selectedItemTemplate={(item) =>
                                item ? (
                                    <div className="flex align-items-center gap-2">
                                        <img
                                            src={item?.profileImage?.image_url || "https://primefaces.org/cdn/primereact/images/avatar/amyelsner.png"}
                                            alt={item.first_name}
                                            style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                                        />
                                        <span>{item.fullName}</span>
                                    </div>
                                ) : ''
                            }
                            placeholder="Search User"
                        />
                    ) : (
                        <div className="mt-3 p-2 border-round surface-100 shadow-1 d-flex align-items-center gap-3 justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                                <img
                                    src={selectedUser?.profileImage?.image_url || "https://primefaces.org/cdn/primereact/images/avatar/amyelsner.png"}
                                    alt={selectedUser.first_name}
                                    style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                                />
                                <div>
                                    <div className="text-muted"><b>{selectedUser.first_name} {selectedUser.last_name}</b></div>
                                    <div className="text-sm text-color-secondary">{selectedUser.email}</div>
                                </div>
                            </div>
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-danger"
                                onClick={() => setSelectedUser(null)}
                                tooltip="Remove Assignee"
                            />
                        </div>
                    )}



                    <Button
                        label="Add SubTask"
                        icon="pi pi-plus"
                        severity="info"
                        className="mt-2"
                        onClick={handleAddChecklist}
                    />
                </div>
            </div>
            <CustomToast ref={toastRef} />
        </div>
    );
};

export default ChecklistPopup;
